import { createFeatureSelector, createSelector } from '@ngrx/store';
import dayjs from 'dayjs';
import { rfisFeatureKey, RFIState } from './rfis.reducer';
import {
  RFI,
  RFI_STATUS,
  RFICompanyGroup,
  RFIListingItem,
  RFIListingItemExtended,
  RFIUser,
} from '@app/store/rfis/rfi.types';

// feature selector
const selectRFIState = createFeatureSelector<RFIState>(rfisFeatureKey);

const extendRFIItem = (rfi: RFIListingItem): RFIListingItemExtended => {
  const dueDate = dayjs.utc(rfi.due_date);
  const submittedAt = dayjs.utc(rfi.submitted_at);
  const now = dayjs.utc();
  const remainingDays = rfi.days_remaining;
  const totalPeriodDays = dueDate.diff(submittedAt, 'days');

  let percentage;
  if (rfi.status === RFI_STATUS.DRAFT) {
    percentage = 0;
  } else if (rfi.status === RFI_STATUS.CLOSED || rfi.status === RFI_STATUS.VOID) {
    percentage = 100;
  } else if (dueDate.isAfter(now) && totalPeriodDays) {
    percentage = ((totalPeriodDays - remainingDays) * 100) / totalPeriodDays;
  } else if (!totalPeriodDays || !rfi.submitted_at) {
    percentage = 0;
  } else {
    percentage = 100;
  }

  // leaving it here if client wants to put it back in the future...
  // let auto_status: RFI_AUTO_STATUS = RFI_AUTO_STATUS.NOT_SUBMITTED;
  // if (rfi.answer_status === 'approved') {
  //   auto_status = RFI_AUTO_STATUS.APPROVED;
  // } else if (rfi.answered_at) {
  //   auto_status = RFI_AUTO_STATUS.ANSWERED;
  // } else if (rfi.responsible_seen_at) {
  //   auto_status = RFI_AUTO_STATUS.SEEN;
  // } else if (rfi.status !== RFI_STATUS.DRAFT) {
  //   auto_status = RFI_AUTO_STATUS.NOT_ANSWERED;
  // }

  return {
    ...rfi,
    remaining_percent: percentage,
    total_period_days: totalPeriodDays,
    // auto_status,
  };
};

// selector definitions with uppercase RFI acronym
const selectAllRFIs = createSelector(selectRFIState, (state) =>
  state.RFIs.map((item) => extendRFIItem(item)),
);
const selectRFIsLoading = createSelector(selectRFIState, (state) => state.loading);
const selectRFICreating = createSelector(selectRFIState, (state) => state.creating);
const selectRFIError = createSelector(selectRFIState, (state) => state.error);

const selectRFIDetailsLoading = createSelector(selectRFIState, (state) => state.loadingDetails);
const selectRFIDetails = createSelector(selectRFIState, (state) => state.selectedRFI);
const selectRFICompanies = createSelector(selectRFIDetails, (rfi: RFI): RFICompanyGroup[] => {
  if (!rfi) {
    return [];
  }
  const companies = new Map<string, Set<RFIUser>>();
  const allUsers = [
    rfi.rfi_originator,
    rfi.rfi_responsible,
    rfi.rfi_manager,
    ...rfi.participants,
  ].filter((user) => !!user);
  for (const user of allUsers) {
    if (!user.company_name) {
      user.company_name = 'Others';
    }

    if (!companies.has(user.company_name)) {
      companies.set(user.company_name, new Set());
    }

    companies.get(user.company_name).add(user);
  }

  return [...companies.entries()].map(([company_name, usersSet]) => {
    return { company_name, users: [...usersSet] };
  });
});

const selectIsUpdatingRFI = (rfiId: number) =>
  createSelector(selectRFIState, (state) => state.updatingIds.includes(rfiId));
const selectIsDeletingRFI = (rfiId: number) =>
  createSelector(selectRFIState, (state) => state.deletingIds.includes(rfiId));

const selectNextRFINumber = createSelector(selectRFIState, (state) => state.nextRFINumber);
const selectRFIFilters = createSelector(selectRFIState, (state) => state.filters);

// exported object aggregating all selectors
export const RFISelectors = {
  selectRFIState,
  selectAllRFIs,
  selectRFIsLoading,
  selectRFICreating,
  selectRFIError,
  selectIsUpdatingRFI,
  selectIsDeletingRFI,

  selectRFIDetailsLoading,
  selectRFIDetails,
  selectRFICompanies,

  selectNextRFINumber,
  selectRFIFilters,
};
